// ===================================================================
// 📊 ÁREA DO ANALISTA - PÁGINA DE ANÁLISE
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { GradeInput } from '../../components/ui/GradeInput';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import { ExemptionAnalysis } from './ExemptionAnalysis';
import { ResourcesAnalysis } from './ResourcesAnalysis';
import { SpecialAttendanceAnalysis } from './SpecialAttendanceAnalysis';
import { isOrdemInscricaoMode, isIntentionLetterMode } from '../../utils/selectionMode';
import {
  IntentionLetterReadOnly,
  IntentionLetterRubricForm,
  validateRubricForm,
} from '../../components/intentionLetter/IntentionLetterFields';
import { formatRegistrationDateTime } from '../../utils/helpers';
import { sortApplicationsForDisplay } from '../../utils/adminTableSort';
import { getPaymentStatusLabel, getAnalysisStatusLabel, getStatusBadgeStyle, getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';

export const AnalystArea = React.memo(({ user, activeTab: activeTabProp }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [applications, setApplications] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [courses, setCourses] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterAnalysis, setFilterAnalysis] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterCampus, setFilterCampus] = useState('');
  const [filterQuota, setFilterQuota] = useState('');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedApplicationForAnalysis, setSelectedApplicationForAnalysis] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [applicationToReturn, setApplicationToReturn] = useState(null);
  
  // Estado da aba ativa (vem da Navigation via prop)
  const activeTab = activeTabProp || 'applications';
  
  // Estados para análise de notas
  const [analysisForm, setAnalysisForm] = useState({
    grades: {},
    status: 'approved',
    rejection_reason: '',
    notes: ''
  });

  // Função para validar se motivo de indeferimento foi selecionado
  const validateRejectionReason = () => {
    if (analysisForm.status === 'rejected' && !analysisForm.rejection_reason) {
      alert('É obrigatório selecionar um motivo de indeferimento quando a situação for "Indeferida"');
      return false;
    }
    return true;
  };

  const { addNotification } = useNotifications();

  const selectedProcessMeta = useMemo(
    () => processes.find((p) => String(p.id) === String(parseAdminSelectedProcessId(selectedProcessId).id)),
    [processes, selectedProcessId],
  );
  const isOrdemInscricaoProcess = isOrdemInscricaoMode(selectedProcessMeta?.selection_mode);
  const processHasFee = !!selectedProcessMeta?.has_fee;
  const processUsesQuotas = selectedProcessMeta?.use_quotas === true;

  useEffect(() => {
    if (!processUsesQuotas && filterQuota) setFilterQuota('');
  }, [processUsesQuotas, filterQuota]);

  const loadData = useCallback(async (isBackground = false) => {
    const { id: processId } = parseAdminSelectedProcessId(selectedProcessId);
    if (processId == null) {
      setApplications([]);
      setAppeals([]);
      if (!isBackground) setLoading(false);
      return;
    }
    try {
      if (!isBackground) setLoading(true);
      const [applicationsData, appealsData, coursesData, reasonsData] = await Promise.all([
        apiClient.getApplications({ forCurricular: true, processId }),
        apiClient.getAppeals({ processId }),
        apiClient.getCourses(processId || undefined),
        apiClient.getRejectionReasons({ context: 'list' })
      ]);
      setApplications(Array.isArray(applicationsData) ? applicationsData : []);
      setAppeals(Array.isArray(appealsData) ? appealsData : []);
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
    } catch (err) {
      if (!isBackground) setError('Erro ao carregar dados');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user?.id, selectedProcessId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasModalOpen = showAnalysisModal || showReturnModal;

  useEffect(() => {
    if (hasModalOpen) return;
    const interval = setInterval(() => loadData(true), 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData, hasModalOpen]);

  // 🔄 Função para forçar recarregamento dos dados
  const refreshData = useCallback(() => {
    console.log('🔄 Forçando recarregamento dos dados do analista...');
    loadData();
  }, [loadData]);

  const getPaymentStatusLabel = useCallback((status) => {
    switch (status) {
      case 'paid':
        return 'Pago';
      case 'pending':
        return 'Pendente';
      case 'pending_exemption':
        return 'Isenção em Análise';
      case 'exempt':
      case 'exempted':
        return 'Isento';
      case 'exemption_appeal_approved':
        return 'Recurso de Isenção Aprovado';
      case 'rejected':
        return 'Rejeitado';
      default:
        return status;
    }
  }, []);

  const getAssignmentStatusLabel = useCallback((status) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'assigned':
        return 'Atribuído';
      case 'analyzing':
        return 'Em Análise';
      case 'completed':
        return 'Concluído';
      case 'approved':
        return 'Aprovado';
      case 'rejected':
        return 'Rejeitado';
      default:
        return status;
    }
  }, []);

  // Função getStatusBadgeStyle agora importada do arquivo padronizado

  const getAnalysisStatusLabel = useCallback((status, application) => {
    if (application?.excluded_at) return 'Excluída';
    // Se não tem analyst_id, significa que não foi atribuída
    if (!application?.analyst_id) {
      return 'Não atribuída';
    }
    
    // Se tem analyst_id mas status é pending, significa que foi atribuída e está pendente
    if (status === 'pending' && application.analyst_id) {
      return 'Pendente';
    }
    
    switch (status) {
      case 'approved':
        return 'Deferida';
      case 'returned':
        return 'Devolvida';
      case 'rejected':
        return 'Indeferida';
      case 'analyzing':
        return 'Em Análise';
      case 'completed':
        return 'Concluído';
      default:
        return 'Não analisada';
    }
  }, []);

  // Função getAnalysisStatusStyle agora importada do arquivo padronizado

  const procSel = parseAdminSelectedProcessId(selectedProcessId);
  const filteredApplications = applications.filter(app => {
    if (procSel.id != null && app.process_id !== procSel.id) return false;
    const process = processes.find(p => p.id === app.process_id);
    if (!process) return false;
    // Só análise curricular: mostrar apenas inscrições de processos com modalidade "Análise Curricular"
    if (process.selection_mode === 'prova') return false;
    // Excluir isenção rejeitada APENAS quando processo cobra taxa e candidato ainda não pagou
    if (process.has_fee && app.exemption_status === 'rejected' && app.payment_status !== 'paid' && !app.excluded_at) {
      return false;
    }
    if (filterAnalysis && app.analysis_status !== filterAnalysis) return false;
    if (filterCourse && String(app.course_name || '') !== String(filterCourse)) return false;
    if (filterCampus && String(app.course_campus || '') !== String(filterCampus)) return false;
    if (filterQuota && String(app.quota_type || 'AC') !== String(filterQuota)) return false;
    return true;
  });

  const displayApplications = useMemo(() => {
    if (!isOrdemInscricaoProcess) return filteredApplications;
    return sortApplicationsForDisplay(filteredApplications, { key: 'registered_at', dir: 'asc' });
  }, [filteredApplications, isOrdemInscricaoProcess]);

  const courseFilterOptions = useMemo(() => {
    const names = new Set();
    applications.forEach((app) => {
      if (procSel.id != null && app.process_id !== procSel.id) return;
      if (app.course_name) names.add(String(app.course_name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, procSel.id]);

  const campusFilterOptions = useMemo(() => {
    const campuses = new Set();
    applications.forEach((app) => {
      if (procSel.id != null && app.process_id !== procSel.id) return;
      if (app.course_campus) campuses.add(String(app.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, procSel.id]);

  const quotaFilterOptions = useMemo(() => {
    const quotas = new Set();
    applications.forEach((app) => {
      if (procSel.id != null && app.process_id !== procSel.id) return;
      quotas.add(String(app.quota_type || 'AC'));
    });
    return Array.from(quotas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, procSel.id]);

  const handleAnalyze = useCallback((application) => {
    setSelectedApplicationForAnalysis(application);
    
    const courseId = parseInt(application.course_id);
    const course = courses.find(c => c.id === courseId);
    const processForApp = processes.find((p) => p.id === application.process_id);
    const letterMode = isIntentionLetterMode(processForApp?.selection_mode);
    const isEdit = application.analysis_status === 'approved' || application.analysis_status === 'rejected';
    
    let initialGrades = {};
    if (letterMode && course?.letter_rubric?.length) {
      course.letter_rubric.forEach((row) => {
        initialGrades[row.id] = isEdit ? 0 : '';
      });
      const savedGrades = application.analysis_grades;
      if (savedGrades && typeof savedGrades === 'object') {
        course.letter_rubric.forEach((row) => {
          if (savedGrades[row.id] !== undefined && savedGrades[row.id] !== null) {
            initialGrades[row.id] = savedGrades[row.id];
          }
        });
      }
    } else if (course && course.subjects) {
      course.subjects.forEach(subject => {
        initialGrades[subject.serie] = {};
        subject.disciplinas.forEach(disciplina => {
          initialGrades[subject.serie][disciplina.name] = isEdit ? 0 : undefined;
        });
      });
    }
    
    const savedGrades = application.analysis_grades;
    if (!letterMode && savedGrades && typeof savedGrades === 'object') {
      Object.keys(savedGrades).forEach(serie => {
        if (initialGrades[serie]) {
          Object.keys(savedGrades[serie] || {}).forEach(disc => {
            if (initialGrades[serie][disc] !== undefined) {
              initialGrades[serie][disc] = savedGrades[serie][disc];
            }
          });
        }
      });
    }
    
    const rawSt = (application.analysis_status || '').toLowerCase();
    const formStatus =
      rawSt === 'rejected' ? 'rejected' : rawSt === 'approved' ? 'approved' : 'approved';
    setAnalysisForm({
      grades: initialGrades,
      status: isEdit ? formStatus : 'approved',
      rejection_reason: isEdit ? (application.curricular_rejection_reason || application.rejection_reason || '') : '',
      notes: isEdit ? (application.curricular_rejection_notes || application.rejection_notes || '') : '',
    });
    
    setShowAnalysisModal(true);
  }, [courses, processes]);

  const handleDownloadDocuments = useCallback(async (application, docIndex = null) => {
    const docs = application.documents || application.curricular_documents || [];
    if (docs.length === 0) {
      addNotification('Aviso', 'Nenhum documento disponível para download', 'warning');
      return;
    }
    const indices = docIndex !== null ? [docIndex] : docs.map((_, i) => i);
    if (docIndex === null) {
      addNotification('Download', `Baixando ${docs.length} documento(s)...`, 'info');
    }
    for (const i of indices) {
      try {
        const blob = await apiClient.downloadDocument({
          applicationId: application.id,
          type: 'curricular',
          index: i
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = docs[i]?.name || `documento_${i + 1}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        addNotification('Erro', err.message || `Falha ao baixar documento ${i + 1}`, 'error');
      }
    }
  }, [addNotification]);

  const handleAcceptCurricularTask = useCallback(async (application) => {
    try {
      const { error } = await apiClient.acceptCurricularAnalysis(application.id);
      if (error) throw new Error(error);
      addNotification('Sucesso', 'Tarefa aceita. Situação: Em Análise', 'success');
      loadData();
    } catch (e) {
      addNotification('Erro', e.message || 'Erro ao aceitar tarefa', 'error');
    }
  }, [addNotification, loadData]);

  const openReturnModal = useCallback((application) => {
    setApplicationToReturn(application);
    setReturnReason('');
    setShowReturnModal(true);
  }, []);

  const handleReturnCurricularTask = useCallback(async () => {
    if (!applicationToReturn) return;
    try {
      const { error } = await apiClient.returnCurricularAnalysis(applicationToReturn.id, returnReason);
      if (error) throw new Error(error);
      addNotification('Sucesso', 'Tarefa devolvida com sucesso', 'success');
      setShowReturnModal(false);
      setApplicationToReturn(null);
      setReturnReason('');
      loadData();
    } catch (e) {
      addNotification('Erro', e.message || 'Erro ao devolver tarefa', 'error');
    }
  }, [applicationToReturn, returnReason, addNotification, loadData]);

  // Função para manipular mudanças nas notas
  const handleGradeChange = useCallback((serie, disciplina, value) => {
    setAnalysisForm(prev => ({
      ...prev,
      grades: {
        ...prev.grades,
        [serie]: {
          ...prev.grades[serie],
          [disciplina]: value
        }
      }
    }));
  }, []);

  // Função para calcular média ponderada
  const calculateWeightedAverage = useCallback((grades, courseSubjects) => {
    console.log('🔍 Debug calculateWeightedAverage - grades:', grades);
    console.log('🔍 Debug calculateWeightedAverage - courseSubjects:', courseSubjects);
    
    if (!grades || !courseSubjects) {
      console.log('❌ Debug calculateWeightedAverage - grades ou courseSubjects vazios');
      return 0;
    }
    
    let totalSeriesAverage = 0;
    let seriesCount = 0;
    
    courseSubjects.forEach(subject => {
      console.log(`🔍 Debug calculateWeightedAverage - Processando série: ${subject.serie}`);
      const serieGrades = grades[subject.serie];
      console.log(`🔍 Debug calculateWeightedAverage - Notas da série ${subject.serie}:`, serieGrades);
      
      if (serieGrades) {
        let serieWeightedSum = 0;
        let serieTotalWeight = 0;
        
        subject.disciplinas.forEach(disciplina => {
          const grade = serieGrades[disciplina.name];
          console.log(`🔍 Debug calculateWeightedAverage - Disciplina ${disciplina.name}: grade=${grade}, weight=${disciplina.weight}`);
          
          if (grade !== undefined && grade !== null && grade !== '' && !isNaN(parseFloat(grade))) {
            serieWeightedSum += parseFloat(grade) * disciplina.weight;
            serieTotalWeight += disciplina.weight;
            console.log(`🔍 Debug calculateWeightedAverage - Soma ponderada: ${serieWeightedSum}, Peso total: ${serieTotalWeight}`);
          }
        });
        
        if (serieTotalWeight > 0) {
          const serieAverage = serieWeightedSum / serieTotalWeight;
          totalSeriesAverage += serieAverage;
          seriesCount++;
          console.log(`🔍 Debug calculateWeightedAverage - Média da série ${subject.serie}: ${serieAverage}`);
        }
      }
    });
    
    const result = seriesCount > 0 ? Math.round((totalSeriesAverage / seriesCount) * 100) / 100 : 0;
    console.log(`🔍 Debug calculateWeightedAverage - Resultado final: ${result} (totalSeriesAverage: ${totalSeriesAverage}, seriesCount: ${seriesCount})`);
    return result;
  }, []);

  // Função para calcular média atual
  const calculateCurrentAverage = useCallback(() => {
    if (!selectedApplicationForAnalysis) return 0;
    
    // Converter course_id para number para comparação correta
    const courseId = parseInt(selectedApplicationForAnalysis.course_id);
    const course = courses.find(c => c.id === courseId);
    
    console.log('🔍 Debug calculateCurrentAverage - Application course_id:', selectedApplicationForAnalysis.course_id);
    console.log('🔍 Debug calculateCurrentAverage - Converted courseId:', courseId);
    console.log('🔍 Debug calculateCurrentAverage - Course encontrado:', course);
    console.log('🔍 Debug calculateCurrentAverage - Course subjects:', course?.subjects);
    console.log('🔍 Debug calculateCurrentAverage - analysisForm.grades:', analysisForm.grades);
    
    if (!course || !course.subjects) {
      console.log('❌ Debug calculateCurrentAverage - Curso ou subjects não encontrados');
      return 0;
    }
    
    const result = calculateWeightedAverage(analysisForm.grades, course.subjects);
    console.log('🔍 Debug calculateCurrentAverage - Resultado:', result);
    return result;
  }, [selectedApplicationForAnalysis, analysisForm.grades, courses, calculateWeightedAverage]);

  // Função para validar se todas as notas foram preenchidas e retornar quais estão faltando
  const validateAllGrades = useCallback((grades, courseSubjects) => {
    if (!courseSubjects || !grades) return { isValid: false, missingGrades: [] };
    
    const missingGrades = [];
    
    for (const subject of courseSubjects) {
      const serieGrades = grades[subject.serie];
      if (!serieGrades) {
        // Se não há notas para esta série, todas as disciplinas estão faltando
        for (const disciplina of subject.disciplinas) {
          missingGrades.push(`${subject.serie} - ${disciplina.name || disciplina.nome || 'Disciplina'}`);
        }
        continue;
      }
      
      for (const disciplina of subject.disciplinas) {
        const discName = disciplina.name || disciplina.nome;
        const grade = serieGrades[discName];
        if (grade === undefined || grade === null || grade === '') {
          missingGrades.push(`${subject.serie} - ${discName || 'Disciplina'}`);
        }
      }
    }
    
    return {
      isValid: missingGrades.length === 0,
      missingGrades: missingGrades
    };
  }, []);

  const getCurricularRowDisplay = useCallback((application) => {
    if (application?.excluded_at) {
      return { displayStatus: 'excluded', label: 'Excluída' };
    }
    const terminal = application?.analysis_status;
    if (terminal === 'approved' || terminal === 'rejected') {
      return {
        displayStatus: terminal,
        label: getAnalysisStatusLabel(terminal, application),
      };
    }
    let curStatus = application.curricular_analysis_status ?? (application.analyst_id ? 'pending' : null);
    if (application.analyst_id && (curStatus === 'rejected' || curStatus === 'approved') &&
        terminal !== 'approved' && terminal !== 'rejected') {
      curStatus = 'pending';
    }
    if (curStatus === 'pending') {
      return { displayStatus: 'pending', label: 'Atribuída' };
    }
    if (curStatus === 'analyzing') {
      return { displayStatus: 'analyzing', label: 'Em Análise' };
    }
    return {
      displayStatus: terminal || 'not_analyzed',
      label: getAnalysisStatusLabel(terminal, application),
    };
  }, [getAnalysisStatusLabel]);

  const handleRubricScoreChange = useCallback((criterionId, value) => {
    setAnalysisForm((prev) => ({
      ...prev,
      grades: {
        ...prev.grades,
        [criterionId]: value,
      },
    }));
  }, []);

  // Função para salvar análise
  const saveAnalysis = useCallback(async () => {
    if (!selectedApplicationForAnalysis) return;
    if (analysisForm.status !== 'approved' && analysisForm.status !== 'rejected') {
      addNotification('Validação', 'Selecione Deferida ou Indeferida.', 'warning');
      return;
    }

    const processForApp = processes.find((p) => p.id === selectedApplicationForAnalysis.process_id);
    const ordemMode = isOrdemInscricaoMode(processForApp?.selection_mode);

    if (analysisForm.status === 'rejected' && !analysisForm.rejection_reason) {
      addNotification('❌ Validação', 'Selecione o motivo de indeferimento.', 'warning');
      return;
    }

    if (ordemMode) {
      try {
        const { data: updatedApp, error: updateError } = await apiClient.updateApplication(selectedApplicationForAnalysis.id, {
          analysis_status: analysisForm.status,
          ...(analysisForm.status === 'rejected' ? {
            ...(analysisForm.rejection_reason ? { rejection_reason: analysisForm.rejection_reason } : {}),
            rejection_notes: (analysisForm.notes || '').trim(),
          } : {}),
        });
        if (updateError) throw new Error(updateError);
        addNotification(
          '✅ Análise Salva',
          analysisForm.status === 'rejected' ? 'Indeferimento registrado.' : 'Documentação deferida.',
          'success',
        );
        setShowAnalysisModal(false);
        setSelectedApplicationForAnalysis(null);
        setAnalysisForm({ grades: {}, status: 'approved', rejection_reason: '', notes: '' });
        if (updatedApp) {
          setApplications((prev) => prev.map((app) => (app.id === selectedApplicationForAnalysis.id ? { ...app, ...updatedApp } : app)));
        }
        await loadData();
      } catch (error) {
        addNotification('❌ Erro', error.message || 'Erro ao salvar análise', 'error');
      }
      return;
    }

    const letterMode = isIntentionLetterMode(processForApp?.selection_mode);
    const courseId = parseInt(selectedApplicationForAnalysis.course_id);
    const course = courses.find((c) => c.id === courseId);

    if (letterMode) {
      if (!course?.letter_rubric?.length) {
        addNotification('❌ Erro', 'Barema não configurado para este curso', 'error');
        return;
      }
      let finalScore = 0;
      if (analysisForm.status !== 'rejected') {
        const rubricCheck = validateRubricForm(course, analysisForm.grades);
        if (!rubricCheck.ok) {
          addNotification('❌ Validação', rubricCheck.message, 'warning');
          return;
        }
        finalScore = rubricCheck.total;
      }
      try {
        if (analysisForm.status !== 'rejected') {
          const { error: analysisError } = await apiClient.createAnalysis({
            application_id: selectedApplicationForAnalysis.id,
            status: analysisForm.status,
            final_score: finalScore,
            grades: analysisForm.grades,
          });
          if (analysisError) throw new Error(analysisError);
        }
        const { data: updatedApp, error: updateError } = await apiClient.updateApplication(selectedApplicationForAnalysis.id, {
          analysis_status: analysisForm.status,
          ...(analysisForm.status === 'rejected' ? {
            ...(analysisForm.rejection_reason ? { rejection_reason: analysisForm.rejection_reason } : {}),
            rejection_notes: (analysisForm.notes || '').trim(),
          } : {}),
        });
        if (updateError) throw new Error(updateError);
        addNotification(
          '✅ Análise Salva',
          analysisForm.status === 'rejected' ? 'Indeferimento registrado.' : `Nota final: ${finalScore.toFixed(2)}`,
          'success',
        );
        setShowAnalysisModal(false);
        setSelectedApplicationForAnalysis(null);
        setAnalysisForm({ grades: {}, status: 'approved', rejection_reason: '', notes: '' });
        if (updatedApp) {
          setApplications((prev) => prev.map((app) => (app.id === selectedApplicationForAnalysis.id ? { ...app, ...updatedApp } : app)));
        }
        await loadData();
      } catch (error) {
        addNotification('❌ Erro', error.message || 'Erro ao salvar análise', 'error');
      }
      return;
    }

    const courseIdCurricular = parseInt(selectedApplicationForAnalysis.course_id);
    const courseCurricular = courses.find((c) => c.id === courseIdCurricular);
    
    console.log('🔍 Debug saveAnalysis - Application course_id:', selectedApplicationForAnalysis.course_id);
    console.log('🔍 Debug saveAnalysis - Converted courseId:', courseIdCurricular);
    console.log('🔍 Debug saveAnalysis - Course encontrado:', courseCurricular);
    console.log('🔍 Debug saveAnalysis - Course subjects:', courseCurricular?.subjects);
    
    if (!courseCurricular || !courseCurricular.subjects) {
      addNotification('❌ Erro', 'Curso não encontrado ou disciplinas não configuradas', 'error');
      return;
    }

    if (analysisForm.status === 'rejected') {
      if (!analysisForm.rejection_reason) {
        addNotification('❌ Validação', 'Selecione o motivo de indeferimento.', 'warning');
        return;
      }
    } else {
      const validation = validateAllGrades(analysisForm.grades, courseCurricular.subjects);
      if (!validation.isValid) {
        const missingList = validation.missingGrades.join('\n• ');
        addNotification('❌ Validação', `Preencha todas as notas das disciplinas (mesmo que seja zero) antes de salvar.\n\nDisciplinas faltando:\n• ${missingList}`, 'error');
        return;
      }
    }

    const finalAverage = calculateWeightedAverage(analysisForm.grades, courseCurricular.subjects);
    
    try {
      const { error: analysisError } = await apiClient.createAnalysis({
        application_id: selectedApplicationForAnalysis.id,
        status: analysisForm.status,
        final_score: finalAverage,
        grades: analysisForm.grades
      });
      if (analysisError) throw new Error(analysisError);
      
      const { data: updatedApp, error: updateError } = await apiClient.updateApplication(selectedApplicationForAnalysis.id, {
        analysis_status: analysisForm.status,
        ...(analysisForm.status === 'rejected' ? {
          ...(analysisForm.rejection_reason ? { rejection_reason: analysisForm.rejection_reason } : {}),
          rejection_notes: (analysisForm.notes || '').trim(),
        } : {}),
      });
      if (updateError) throw new Error(updateError);
      addNotification(
        '✅ Análise Salva',
        analysisForm.status === 'rejected' ? 'Indeferimento registrado.' : `Média final: ${finalAverage.toFixed(2)}`,
        'success'
      );
      setShowAnalysisModal(false);
      setSelectedApplicationForAnalysis(null);
      setAnalysisForm({
        grades: {},
        status: 'approved',
        rejection_reason: '',
        notes: ''
      });
      if (updatedApp) {
        setApplications(prev => prev.map(app =>
          app.id === selectedApplicationForAnalysis.id ? { ...app, ...updatedApp } : app
        ));
      }
      await loadData();
    } catch (error) {
      addNotification('❌ Erro', 'Erro ao salvar análise', 'error');
    }
  }, [selectedApplicationForAnalysis, analysisForm, courses, processes, calculateWeightedAverage, validateAllGrades, addNotification, loadData]);

  if (loading && applications.length === 0 && !hasModalOpen) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.contentContainer}>
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Conteúdo baseado na aba ativa */}
      {activeTab === 'applications' && (
        <>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
            📝 Análise de Inscrições
          </h2>
          {/* Filtros */}
      <div style={{ ...styles.card, ...styles.mb6 }}>
        <h3 style={styles.cardTitle}>🔍 Filtros</h3>
        <div style={{ ...styles.flex, ...styles.gap4, ...styles.flexWrap }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Processo Seletivo</label>
            <select
              value={selectedProcessId}
              onChange={setSelectedProcessId}
              style={styles.select}
            >
              <option value="">Selecione um processo</option>
              {processes.filter(p => p.selection_mode !== 'prova').map(process => (
                <option key={process.id} value={process.id}>
                  {process.name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Situação da Análise</label>
            <select
              value={filterAnalysis}
              onChange={(e) => setFilterAnalysis(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas as situações</option>
              <option value="pending">Pendente</option>
              <option value="approved">Deferida</option>
              <option value="rejected">Indeferida</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Curso</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
              <option value="">Todos os cursos</option>
              {courseFilterOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidade</label>
            <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
              <option value="">Todas as unidades</option>
              {campusFilterOptions.map((campus) => (
                <option key={campus} value={campus}>{campus}</option>
              ))}
            </select>
          </div>
          {processUsesQuotas && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Cota</label>
            <select value={filterQuota} onChange={(e) => setFilterQuota(e.target.value)} style={styles.select}>
              <option value="">Todas as cotas</option>
              {quotaFilterOptions.map((quota) => (
                <option key={quota} value={quota}>{quota}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </div>

      {/* Legenda de ícones */}
      <div style={{ ...styles.card, marginBottom: '12px', padding: '14px 20px', backgroundColor: '#F9FAFB' }}>
        <div style={{ fontSize: '15px', color: '#374151', display: 'flex', flexWrap: 'wrap', gap: '14px 24px' }}>
          <span><strong>Legenda:</strong></span>
          <span>{ACTION_ICONS.analyze} Analisar</span>
          <span>{ACTION_ICONS.edit} Editar</span>
        </div>
      </div>

      {/* Inscrições */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={styles.cardTitle}>Inscrições Atribuídas</h3>
          <span style={{ fontSize: '14px', color: '#6B7280' }}>
            Total: {filteredApplications.length}
          </span>
        </div>

        {filteredApplications.length > 0 ? (
          <div style={{ ...styles.tableContainer, ...styles.tableScrollContainer }}>
            <table style={{ ...styles.table, width: '100%', fontSize: '10px', hyphens: 'none' }}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '9px', padding: '8px 6px', width: '45px' }}>Nº</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Candidato</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Curso</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Unidade</th>
                  {processUsesQuotas && (
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Cota</th>
                  )}
                  {processHasFee && (
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Pagamento</th>
                  )}
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Situação da Análise</th>
                  {isOrdemInscricaoProcess && (
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px', whiteSpace: 'nowrap' }}>Inscrição</th>
                  )}
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {displayApplications.map((application) => {
                  const process = processes.find(p => p.id === application.process_id);
                  return (
                    <tr key={application.id}>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 6px', fontWeight: '600', color: '#374151' }}>
                        #{application.id}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '10px' }}>
                          {application.candidate_name}
                        </div>
                        <div style={{ fontSize: '9px', color: '#6B7280' }}>
                          CPF: {application.candidate_cpf || 'N/A'}
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                            {application.course_name || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>{application.course_campus || '—'}</td>
                      {processUsesQuotas && (
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>{application.quota_type || 'AC'}</td>
                      )}
                      {processHasFee && (
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <span style={{
                          ...getStatusBadgeStyle(
                            application.exemption_status === 'appeal_approved' ? 'exemption_appeal_approved' :
                            application.exemption_status === 'approved' ? 'exempted' :
                            application.payment_status || 'pending'
                          ),
                          ...styles.tableCellBadge,
                          fontSize: '9px',
                          padding: '4px 8px',
                          whiteSpace: 'nowrap',
                        }} title={getPaymentStatusLabel(
                          application.exemption_status === 'appeal_approved' ? 'exemption_appeal_approved' :
                          application.exemption_status === 'approved' ? 'exempted' :
                          application.payment_status || 'pending'
                        )}>
                          {getPaymentStatusLabel(
                            application.exemption_status === 'appeal_approved' ? 'exemption_appeal_approved' :
                            application.exemption_status === 'approved' ? 'exempted' :
                            application.payment_status || 'pending'
                          )}
                        </span>
                      </td>
                      )}
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        {(() => {
                          const { displayStatus, label } = getCurricularRowDisplay(application);
                          return (
                            <span style={{ ...getStatusBadgeStyle(displayStatus), ...styles.tableCellBadge, fontSize: '9px', padding: '4px 8px' }} title={label}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      {isOrdemInscricaoProcess && (
                      <td
                        style={{ ...styles.tableCell, fontSize: '9px', padding: '8px 10px', whiteSpace: 'nowrap' }}
                        title={application.created_at ? `Registro no sistema (UTC): ${application.created_at}` : ''}
                      >
                        {formatRegistrationDateTime(application.created_at)}
                      </td>
                      )}
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {(() => {
                            if (application.excluded_at) return <span style={{ fontSize: '12px', color: '#6B7280' }}>—</span>;
                            if (application.analysis_status === 'approved' || application.analysis_status === 'rejected') {
                              return (
                                <button onClick={() => handleAnalyze(application)} style={getButtonStyle('secondary', 'small')} title="Editar análise">{ACTION_ICONS.edit} Editar</button>
                              );
                            }
                            let curStatus = application.curricular_analysis_status ?? (application.analyst_id ? 'pending' : null);
                            if (application.analyst_id && (curStatus === 'rejected' || curStatus === 'approved')) curStatus = 'pending';
                            const appProcess = processes.find((p) => p.id === application.process_id);
                            const hideReturn = isIntentionLetterMode(appProcess?.selection_mode);
                            if (curStatus === 'pending') return (
                              <>
                                <button onClick={() => handleAcceptCurricularTask(application)} style={getButtonStyle('primary', 'small')} title="Aceitar tarefa (muda para Em Análise)">✅ Aceitar</button>
                                {!hideReturn && (
                                <button onClick={() => openReturnModal(application)} style={getButtonStyle('secondary', 'small')} title="Devolver tarefa para o admin">↩️ Devolver</button>
                                )}
                                <button disabled style={{ ...getButtonStyle('secondary', 'small'), opacity: 0.5, cursor: 'not-allowed' }} title="Aceite a tarefa para analisar">{ACTION_ICONS.analyze}</button>
                              </>
                            );
                            if (curStatus === 'analyzing') return (
                              <>
                                {!hideReturn && (
                                <button onClick={() => openReturnModal(application)} style={getButtonStyle('secondary', 'small')} title="Devolver tarefa para o admin">↩️ Devolver</button>
                                )}
                                <button onClick={() => handleAnalyze(application)} style={getButtonStyle('primary', 'small')} title="Analisar">{ACTION_ICONS.analyze}</button>
                              </>
                            );
                            if (!application.analyst_id) return (
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>Aguardando atribuição</span>
                            );
                            return null;
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p>Nenhuma inscrição atribuída encontrada</p>
          </div>
        )}
      </div>

      <style>{`
        input[data-grade-input]::placeholder {
          color: #D1D5DB;
          opacity: 0.8;
        }
      `}</style>
      {/* Modal de Análise */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title={isIntentionLetterMode(processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id)?.selection_mode)
          ? 'Análise — Carta de intenção'
          : 'Análise Curricular'}
        large
      >
        {selectedApplicationForAnalysis && (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>
                Informações da Inscrição
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <strong>Candidato:</strong> {selectedApplicationForAnalysis.candidate_name}
                </div>
                <div>
                  <strong>CPF:</strong> {selectedApplicationForAnalysis.candidate_cpf || 'Não informado'}
                </div>
                <div>
                  <strong>Curso:</strong> {selectedApplicationForAnalysis.course_name}<br/>
                  <strong>Unidade:</strong> {selectedApplicationForAnalysis.course_campus || '—'}
                </div>
                <div>
                  <strong>Processo:</strong> {processes.find(p => p.id === selectedApplicationForAnalysis.process_id)?.name || `Processo ${selectedApplicationForAnalysis.process_id}`}
                </div>
              </div>
            </div>

            {isIntentionLetterMode(processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id)?.selection_mode) ? (
              <IntentionLetterReadOnly
                text={selectedApplicationForAnalysis.intention_letter_text}
                course={courses.find((c) => c.id === parseInt(selectedApplicationForAnalysis.course_id, 10))}
              />
            ) : (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', color: '#374151' }}>
                  Documentos Anexados
                </h4>
                {[...(selectedApplicationForAnalysis?.curricular_documents || []), ...(selectedApplicationForAnalysis?.documents || [])].length > 0 && (
                  <button
                    onClick={() => handleDownloadDocuments(selectedApplicationForAnalysis)}
                    style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                  >
                    📥 Baixar Todos
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(() => {
                  const curricularDocs = selectedApplicationForAnalysis.curricular_documents || [];
                  const regularDocs = selectedApplicationForAnalysis.documents || [];
                  const allDocs = [...curricularDocs, ...regularDocs];
                  return allDocs;
                })()?.map((doc, docIndex) => {
                  // Documento é "novo" se foi enviado após a última análise
                  const lastAnalysisDate = selectedApplicationForAnalysis.analyzed_at || selectedApplicationForAnalysis.created_at;
                  const isNewDocument = doc.uploadedAt && new Date(doc.uploadedAt) > new Date(lastAnalysisDate);
                  const uploadDate = doc.uploadedAt ? new Date(doc.uploadedAt) : null;
                  
                  return (
                    <div key={docIndex} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 12px',
                      backgroundColor: isNewDocument ? '#F0F9FF' : '#F9FAFB',
                      borderRadius: '6px',
                      border: `1px solid ${isNewDocument ? '#0EA5E9' : '#E5E7EB'}`,
                      position: 'relative'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '500' }}>{doc.name}</span>
                          {isNewDocument && (
                            <span style={{
                              backgroundColor: '#0EA5E9',
                              color: 'white',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '600'
                            }}>
                              NOVO
                            </span>
                          )}
                        </div>
                        {uploadDate && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6B7280',
                            marginTop: '2px'
                          }}>
                            📅 Enviado em: {uploadDate.toLocaleString('pt-BR')}
                            {doc.size && (
                              <span style={{ marginLeft: '8px' }}>
                                📏 {(doc.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleDownloadDocuments(selectedApplicationForAnalysis, docIndex)}
                        style={{
                          ...styles.button,
                          ...styles.buttonSecondary,
                          ...styles.buttonSmall
                        }}
                      >
                        📄 Download
                      </button>
                    </div>
                  );
                }) || (
                  <p style={{ color: '#6B7280', fontStyle: 'italic' }}>
                    Nenhum documento anexado
                  </p>
                )}
              </div>
            </div>
            )}

            {/* Lançamento de Notas — não se aplica à ordem de inscrição */}
            {!isOrdemInscricaoMode(processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id)?.selection_mode) && (
            <>
            {isIntentionLetterMode(processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id)?.selection_mode) ? (
              <div style={{ marginBottom: '24px' }}>
                <IntentionLetterRubricForm
                  course={courses.find((c) => c.id === parseInt(selectedApplicationForAnalysis.course_id, 10))}
                  scores={analysisForm.grades}
                  onScoreChange={handleRubricScoreChange}
                  disabled={analysisForm.status === 'rejected'}
                />
              </div>
            ) : (
            <>
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>
                📊 Lançamento de Notas
              </h4>
              
              {/* Aviso de disciplinas faltando */}
              {(() => {
                const courseId = parseInt(selectedApplicationForAnalysis.course_id);
                const course = courses.find(c => c.id === courseId);
                
                if (!course?.subjects) return null;
                
                const validation = validateAllGrades(analysisForm.grades, course.subjects);
                
                if (!validation.isValid) {
                  return (
                    <div style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '16px' }}>⚠️</span>
                        <span style={{ fontWeight: '600', color: '#DC2626' }}>
                          Disciplinas não preenchidas:
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#991B1B' }}>
                        {validation.missingGrades.map((missing, index) => (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            • {missing}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: '12px', color: '#991B1B', marginTop: '8px', fontStyle: 'italic' }}>
                        💡 Preencha todas as notas (mesmo que seja zero) para poder deferir a análise.
                      </div>
                    </div>
                  );
                }
                
                return null;
              })()}
              {(() => {
                // Converter course_id para number para comparação correta
                const courseId = parseInt(selectedApplicationForAnalysis.course_id);
                const course = courses.find(c => c.id === courseId);
                
                if (!course?.subjects) {
                  return (
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#FEF3C7', 
                      borderRadius: '8px',
                      border: '1px solid #F59E0B'
                    }}>
                      <p style={{ margin: '0', color: '#92400E', fontSize: '14px' }}>
                        ⚠️ Disciplinas não configuradas para este curso (ID: {selectedApplicationForAnalysis.course_id})
                      </p>
                      <p style={{ margin: '8px 0 0 0', color: '#92400E', fontSize: '12px' }}>
                        Curso encontrado: {course ? course.name : 'Não encontrado'}
                      </p>
                    </div>
                  );
                }
                
                return course.subjects.map((subject, serieIndex) => (
                  <div key={serieIndex} style={{
                    backgroundColor: '#FEF7F0',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    border: '1px solid #FED7AA'
                  }}>
                    <h5 style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#EA580C', 
                      marginBottom: '12px' 
                    }}>
                      📖 {subject.serie}
                    </h5>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '12px' 
                    }}>
                      {subject.disciplinas.map((disciplina, discIndex) => (
                        <div key={discIndex} style={{
                          backgroundColor: 'white',
                          padding: '12px',
                          borderRadius: '6px',
                          border: '1px solid #FED7AA'
                        }}>
                          <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#9A3412',
                            marginBottom: '4px'
                          }}>
                            {disciplina.name} (Peso: {disciplina.weight})
                          </label>
                          <GradeInput
                            value={analysisForm.grades[subject.serie]?.[disciplina.name]}
                            onChange={(v) => handleGradeChange(subject.serie, disciplina.name, v)}
                            style={styles.gradeInput}
                            placeholder="0,00"
                            max={100}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Cálculo da Média */}
            <div style={{
              backgroundColor: '#F0FDF4',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #BBF7D0',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#166534', margin: 0 }}>
                  🧮 Média Ponderada Final
                </h4>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#166534' }}>
                  {calculateCurrentAverage().toFixed(2)}
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#15803D', marginTop: '8px', margin: 0 }}>
                💡 Fórmula: (Média Ponderada Série 1 + Média Ponderada Série 2 + ...) ÷ Número de Séries
              </p>
            </div>
            </>
            )}
            </>
            )}

            {isOrdemInscricaoMode(processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id)?.selection_mode) && (
              <div style={{
                backgroundColor: '#ECFDF5',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #BBF7D0',
                marginBottom: '24px',
                fontSize: '14px',
                color: '#047857',
                lineHeight: 1.5,
              }}>
                <strong>Ordem de inscrição:</strong> verifique os documentos enviados e deferir ou indeferir a inscrição.
                A classificação por vaga seguirá a <strong>ordem cronológica da inscrição</strong> entre os deferidos.
              </div>
            )}

            {/* Situação e Observações */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={styles.label}>📊 Situação da Análise</label>
                  <select
                    value={analysisForm.status === 'rejected' ? 'rejected' : 'approved'}
                    onChange={(e) => setAnalysisForm({
                      ...analysisForm,
                      status: e.target.value,
                      ...(e.target.value === 'approved' ? { rejection_reason: '' } : {})
                    })}
                    style={styles.select}
                  >
                    <option value="approved">✅ Deferida</option>
                    <option value="rejected">❌ Indeferida</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>
                    ❌ Motivo de Indeferimento <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <select
                    value={analysisForm.rejection_reason}
                    onChange={(e) => setAnalysisForm({
                      ...analysisForm, 
                      rejection_reason: e.target.value
                    })}
                    style={styles.select}
                    disabled={analysisForm.status !== 'rejected'}
                  >
                    <option value="">Selecione um motivo...</option>
                    {rejectionReasons.map(reason => (
                      <option key={reason.id} value={reason.id}>
                        {reason.title}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    Obrigatório quando a situação for "Indeferida"
                  </div>
                </div>
              </div>
              
              <div>
                <label style={styles.label}>📝 Observações da Análise</label>
                <textarea
                  value={analysisForm.notes}
                  onChange={(e) => setAnalysisForm({...analysisForm, notes: e.target.value})}
                  style={styles.textarea}
                  placeholder="Observações sobre a análise da inscrição..."
                  rows="3"
                />
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => setShowAnalysisModal(false)}
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary
                }}
              >
                ❌ Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (validateRejectionReason()) {
                    saveAnalysis();
                  }
                }}
                disabled={(() => {
                  const processForApp = processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id);
                  const ordemMode = isOrdemInscricaoMode(processForApp?.selection_mode);
                  if (ordemMode) {
                    if (analysisForm.status === 'rejected') return !analysisForm.rejection_reason;
                    return analysisForm.status !== 'approved' && analysisForm.status !== 'rejected';
                  }
                  const courseId = parseInt(selectedApplicationForAnalysis?.course_id);
                  const course = courses.find(c => c.id === courseId);
                  if (!course?.subjects) return true;
                  if (analysisForm.status === 'rejected') {
                    return !analysisForm.rejection_reason;
                  }
                  const val = validateAllGrades(analysisForm.grades, course.subjects);
                  return !val.isValid;
                })()}
                style={{
                  ...styles.button,
                  ...styles.buttonPrimary,
                  opacity: (() => {
                    const processForApp = processes.find((p) => p.id === selectedApplicationForAnalysis?.process_id);
                    const ordemMode = isOrdemInscricaoMode(processForApp?.selection_mode);
                    if (ordemMode) {
                      if (analysisForm.status === 'rejected') return analysisForm.rejection_reason ? 1 : 0.5;
                      return (analysisForm.status === 'approved' || analysisForm.status === 'rejected') ? 1 : 0.5;
                    }
                    const courseId = parseInt(selectedApplicationForAnalysis?.course_id);
                    const course = courses.find(c => c.id === courseId);
                    if (!course?.subjects) return 0.5;
                    if (analysisForm.status === 'rejected') {
                      return analysisForm.rejection_reason ? 1 : 0.5;
                    }
                    const val = validateAllGrades(analysisForm.grades, course.subjects);
                    return val.isValid ? 1 : 0.5;
                  })()
                }}
              >
                💾 Salvar Análise
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Devolver tarefa (análise curricular) */}
      <Modal
        isOpen={showReturnModal}
        onClose={() => { setShowReturnModal(false); setApplicationToReturn(null); setReturnReason(''); }}
        title="↩️ Devolver tarefa"
      >
        {applicationToReturn && (
          <div>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#6B7280' }}>
              Esta tarefa voltará para a fila de atribuição do admin. O motivo será exibido quando o admin for reatribuir.
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Motivo da devolução (opcional)</label>
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                style={styles.textarea}
                placeholder="Ex.: Documentos incompletos, necessidade de mais informações..."
                rows="4"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowReturnModal(false); setApplicationToReturn(null); setReturnReason(''); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancelar
              </button>
              <button
                onClick={handleReturnCurricularTask}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                Devolver
              </button>
            </div>
          </div>
        )}
      </Modal>
        </>
      )}

      {/* Aba de Análise de Isenções */}
      {activeTab === 'exemptions' && (
        <ExemptionAnalysis user={user} />
      )}

      {activeTab === 'special-attendance' && (
        <SpecialAttendanceAnalysis user={user} />
      )}

      {/* Aba de Análise de Recursos */}
      {activeTab === 'resources' && (
        <ResourcesAnalysis user={user} />
      )}
    </div>
  );
}); 